import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Building2, UserRound, Users } from 'lucide-react';
import { useUser } from '@/shared/hooks/useAuth';
import { dashboardLayoutFor } from './dashboard-layouts';
import { resolveDashboard } from './dashboard-switch';
import type { ViewLevel } from './dashboard-resolution';
import { DashboardLayoutEngine } from './infrastructure/layout/DashboardLayoutEngine';
import { registerDashboardSurfaces } from './widgets/surfaces/register';
import type { DashboardLayoutConfig } from './infrastructure/contracts/layout.contract';

registerDashboardSurfaces();

const LEVEL_LABEL: Record<ViewLevel, string> = { own: 'My dashboard', department: 'Team dashboard', company: 'Company overview' };
const LEVEL_ICON: Record<ViewLevel, typeof Building2> = { own: UserRound, department: Users, company: Building2 };

/**
 * The one dashboard surface. What a person sees is decided by
 * `dashboard-switch.ts` from their authority level and grants — never by who
 * they are.
 */
export function RoleDashboard() {
  const user = useUser();
  const decision = useMemo(() => resolveDashboard(user), [user]);
  const [override, setOverride] = useState<ViewLevel | null>(null);

  if (decision.kind === 'redirect') return <Navigate to={decision.to!} replace />;
  const baseLevel = decision.level!;
  const level = override ?? baseLevel;
  const department = String(user?.department ?? '');
  const layout = level === baseLevel
    ? decision.layout!
    : dashboardLayoutFor(level, department);

  const choices: ViewLevel[] = decision.dualView && baseLevel !== 'own'
    ? [baseLevel, 'own']
    : [];

  return (
    <div className="space-y-4">
      {/*
        No title or greeting here on purpose: every surface already opens with
        its own hero ("Welcome back, …", "October 2026"), so a header from the
        shell printed a second name and a second greeting directly above it.
        The shell contributes only the control the surfaces cannot have
        themselves — the Own / Team switch — and only when the account earns it.
      */}
      {choices.length > 1 ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
          {/* Admin / Super Admin: the overall (company or team) dashboard, or their own. */}
          <span className="text-label-caps uppercase tracking-wider text-on-surface-subtle">Viewing</span>
          <div role="group" aria-label="Dashboard view" className="inline-flex rounded-card-sm border border-border bg-card p-1 shadow-xs">
            {choices.map((v) => {
              const Icon = LEVEL_ICON[v];
              return (
                <button
                  key={v}
                  type="button"
                  aria-pressed={level === v}
                  onClick={() => setOverride(v === baseLevel ? null : v)}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-body-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${level === v ? 'bg-primary text-on-primary shadow-xs' : 'text-on-surface-variant hover:bg-surface-variant hover:text-on-surface'}`}
                >
                  <Icon className="h-4 w-4" aria-hidden />
                  {LEVEL_LABEL[v]}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      <DashboardLayoutEngine
        layout={layout as unknown as DashboardLayoutConfig}
        visibility={{ user, level }}
      />
    </div>
  );
}
